import { inject, Injectable, InjectionToken } from '@angular/core';

import { DatadogLogger, DatadogLoggerFactory, DatadogRumService } from '@ninety/mobile/platform/services';

import { SignOutReason } from '../model/tokens';
import { AuthorizationErrorSummary } from '../oauth/authorization-request';

import {
  AuthDuration,
  AuthFailureLabel,
  AuthTelemetry,
  BiometricOfferAnswer,
  CoalescedTrigger,
  CredentialStoreOperation,
  DurationLabels,
  DurationOutcome,
  SessionOrigin,
} from './auth-telemetry';

/**
 * A logger named `auth`, so auth logs are one facet away in Datadog rather than mixed into the
 * root `AppModule` logger.
 */
export const AUTH_LOGGER = new InjectionToken<DatadogLogger>('AuthLogger', {
  providedIn: 'root',
  factory: () => DatadogLoggerFactory('auth'),
});

/**
 * Everything this adapter sends, under one `auth` key.
 */
function authContext(fields: Record<string, unknown>, failure?: AuthFailureLabel): Record<string, unknown> {
  return { auth: { ...fields, ...(failure ? { failure } : {}) } };
}

/**
 * Datadog implementation of {@link AuthTelemetry}.
 *
 * Deliberately dumb: it chooses a level and a destination and does nothing else. All of the
 * "when is this emitted" logic lives in `AuthTelemetryReporter`, where it is testable without
 * Datadog.
 *
 * Levels are chosen for alerting, not for volume. `illegal` is a warning because races are
 * expected but a spike is a bug; `securityEvent` and `commandFailed` are errors because a
 * non-zero rate means something is wrong right now.
 */
@Injectable()
export class DatadogAuthTelemetry implements AuthTelemetry {
  private readonly logger = inject(AUTH_LOGGER);
  private readonly rum = inject(DatadogRumService);

  transition(from: string, to: string, event: string, failure?: AuthFailureLabel): void {
    this.logger.info({ message: 'auth.transition', data: authContext({ from, to, event }, failure) });
  }

  illegal(from: string, event: string): void {
    this.logger.warn({ message: 'auth.illegal_transition', data: authContext({ from, event }) });
  }

  securityEvent(failure: AuthFailureLabel): void {
    this.logger.error({ message: 'auth.security_event', data: authContext({}, failure) });
    // Also a RUM action, so it lands on the session timeline next to what the person was doing.
    this.rum.addAction('auth.security_event', authContext({}, failure));
  }

  /**
   * Warn rather than error: `access_denied` is someone changing their
   * mind at the provider's screen. A spike in `server_error` or `invalid_request` is the signal worth
   * looking at, and both are visible by grouping on `@auth.code`.
   */
  authorizationError(summary: AuthorizationErrorSummary): void {
    this.logger.warn({ message: 'auth.authorization_error', data: authContext({ ...summary }) });
  }

  sessionEstablished(origin: SessionOrigin): void {
    this.logger.info({ message: 'auth.session_established', data: authContext({ origin }) });
    // Left unprefixed: this describes the RUM session rather than one auth signal, and it is read by
    // filtering any view of the app by whether the person was signed in.
    this.rum.setGlobalContextProperty('authenticated', true);
  }

  signedOut(reason: SignOutReason): void {
    this.logger.info({ message: 'auth.logout', data: authContext({ reason }) });
    this.rum.setGlobalContextProperty('authenticated', false);
  }

  coalescedRefresh(trigger: CoalescedTrigger): void {
    this.logger.debug({ message: 'auth.refresh.coalesced', data: authContext({ trigger }) });
  }

  commandFailed(command: string): void {
    this.logger.error({ message: 'auth.command_failed', data: authContext({ command }) });
  }

  /**
   * Also a RUM action, so the offer lands on the session timeline beside the sign-in it followed.
   *
   * `offered` and the three answers go to one message with an `answer` facet rather than to four
   * messages, so the acceptance rate is a group-by rather than a join.
   */
  biometricOffer(answer: BiometricOfferAnswer): void {
    this.logger.info({ message: 'auth.biometric_offer', data: authContext({ answer }) });
    this.rum.addAction('auth.biometric_offer', authContext({ answer }));
  }

  /**
   * Error level: nothing here fails a sign-in or shows on screen, so nothing else will report it.
   *
   * Both fields are on the log rather than in the message so one query covers the family and can still
   * be split by operation.
   */
  credentialStoreFailed(operation: CredentialStoreOperation, detail: string): void {
    this.logger.error({ message: 'auth.credential_store_failed', data: authContext({ operation, detail }) });
  }

  startDuration(name: AuthDuration): void {
    this.rum.startDurationVital(name);
  }

  /** `labels` is spread as-is: {@link DurationLabels} only holds keys that have a value. */
  stopDuration(
    name: AuthDuration,
    outcome: DurationOutcome,
    failure?: AuthFailureLabel,
    labels?: DurationLabels
  ): void {
    this.rum.stopDurationVital(name, { context: authContext({ outcome, ...labels }, failure) });
  }
}
